import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateProjectsOptions,
  BulkUpdateProjectsResponse,
  DeleteProjectResponse,
  GetProjectOptions,
  ListProjectsOptions,
  ListProjectsResponse,
  Project,
  ProjectCreatedEnvelope,
  ProjectDeletedEnvelope,
  ProjectQuery,
  ProjectUpdatedEnvelope,
  ProjectsQueryBuilder,
  QueryProjectWithCollectionInfoResponse,
  QueryProjectsOptions,
  QueryProjectsResponse,
  QueryProjectsWithCollectionInfoOptions,
  QueryV2,
  UpdateProject,
  UpdateProjectOrderInCollectionIdentifiers,
  UpdateProjectOrderInCollectionResponse,
  bulkUpdateProjects as universalBulkUpdateProjects,
  createProject as universalCreateProject,
  deleteProject as universalDeleteProject,
  getProject as universalGetProject,
  listProjects as universalListProjects,
  queryProjects as universalQueryProjects,
  queryProjectsWithCollectionInfo as universalQueryProjectsWithCollectionInfo,
  typedQueryProjects as universalTypedQueryProjects,
  updateProject as universalUpdateProject,
  updateProjectOrderInCollection as universalUpdateProjectOrderInCollection,
} from './portfolio-projects-v1-project-projects.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/portfolio' };

export function createProject(httpClient: HttpClient): CreateProjectSignature {
  return (project: Project) =>
    universalCreateProject(
      project,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateProjectSignature {
  /**
   * Creates a project.
   * @param - Project to create.
   * @returns Created project.
   */
  (project: Project): Promise<
    NonNullablePaths<
      Project,
      | `coverImage.focalPoint.x`
      | `coverImage.focalPoint.y`
      | `collectionIds`
      | `details`
      | `details.${number}.text`
      | `details.${number}.label`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`
      | `watermark.position`
      | `watermark.size`
      | `watermark.opacity`
      | `watermark.imageUrl`
      | `watermark.enabled`,
      6
    >
  >;
}

export function getProject(httpClient: HttpClient): GetProjectSignature {
  return (projectId: string, options?: GetProjectOptions) =>
    universalGetProject(
      projectId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetProjectSignature {
  /**
   * Retrieves a project.
   * @param - ID of the project to retrieve.
   * @returns The requested project.
   */
  (projectId: string, options?: GetProjectOptions): Promise<
    NonNullablePaths<
      Project,
      | `coverImage.focalPoint.x`
      | `coverImage.focalPoint.y`
      | `collectionIds`
      | `details`
      | `details.${number}.text`
      | `details.${number}.label`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`
      | `watermark.position`
      | `watermark.size`
      | `watermark.opacity`
      | `watermark.imageUrl`
      | `watermark.enabled`,
      6
    >
  >;
}

export function listProjects(httpClient: HttpClient): ListProjectsSignature {
  return (options?: ListProjectsOptions) =>
    universalListProjects(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListProjectsSignature {
  /**
   * Retrieves a list of all projects in a portfolio.
   */
  (options?: ListProjectsOptions): Promise<
    NonNullablePaths<
      ListProjectsResponse,
      | `projects`
      | `projects.${number}.coverImage.focalPoint.x`
      | `projects.${number}.coverImage.focalPoint.y`
      | `projects.${number}.seoData.settings.preventAutoRedirect`
      | `projects.${number}.watermark.position`
      | `projects.${number}.watermark.size`
      | `projects.${number}.watermark.opacity`
      | `projects.${number}.watermark.imageUrl`
      | `projects.${number}.watermark.enabled`,
      6
    >
  >;
}

export function updateProject(httpClient: HttpClient): UpdateProjectSignature {
  return (
    _id: string,
    project: NonNullablePaths<UpdateProject, `revision`, 2>
  ) =>
    universalUpdateProject(
      _id,
      project,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateProjectSignature {
  /**
   * Updates a project.
   * @param - Project ID.
   * @returns Updated project.
   */
  (
    _id: string,
    project: NonNullablePaths<UpdateProject, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      Project,
      | `coverImage.focalPoint.x`
      | `coverImage.focalPoint.y`
      | `collectionIds`
      | `details`
      | `details.${number}.text`
      | `details.${number}.label`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`
      | `watermark.position`
      | `watermark.size`
      | `watermark.opacity`
      | `watermark.imageUrl`
      | `watermark.enabled`,
      6
    >
  >;
}

export function bulkUpdateProjects(
  httpClient: HttpClient
): BulkUpdateProjectsSignature {
  return (
    options?: NonNullablePaths<
      BulkUpdateProjectsOptions,
      | `projects.${number}.project`
      | `projects.${number}.project._id`
      | `projects.${number}.project.revision`,
      5
    >
  ) =>
    universalBulkUpdateProjects(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateProjectsSignature {
  /**
   * Updates multiple projects.
   *
   * To update a single project, call Update Project.
   */
  (
    options?: NonNullablePaths<
      BulkUpdateProjectsOptions,
      | `projects.${number}.project`
      | `projects.${number}.project._id`
      | `projects.${number}.project.revision`,
      5
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdateProjectsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.project.watermark.position`
      | `results.${number}.project.watermark.size`
      | `results.${number}.project.watermark.opacity`
      | `results.${number}.project.watermark.imageUrl`
      | `results.${number}.project.watermark.enabled`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function deleteProject(httpClient: HttpClient): DeleteProjectSignature {
  return (projectId: string) =>
    universalDeleteProject(
      projectId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteProjectSignature {
  /**
   * Deletes a project.
   * @param - ID of the project to delete.
   */
  (projectId: string): Promise<
    NonNullablePaths<DeleteProjectResponse, `projectId`, 2>
  >;
}

export function queryProjects(httpClient: HttpClient): QueryProjectsSignature {
  return (options?: QueryProjectsOptions) =>
    universalQueryProjects(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryProjectsSignature {
  /**
   * Creates a query to retrieve a list of projects.
   *
   * The `queryProjects()` method builds a query to retrieve a list of projects and returns a `ProjectsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-builder/find) method.
   *
   * You can refine the query by chaining `ProjectsQueryBuilder` methods onto the query. `ProjectsQueryBuilder` methods enable you to filter, sort, and control the results that `queryProjects()` returns.
   *
   * `queryProjects()`  runs with the following `ProjectsQueryBuilder` defaults which you can override:
   *
   * + [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-builder/limit)
   * + [`ascending('_id')`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-builder/ascending)
   *
   * The following `ProjectsQueryBuilder` methods are supported for `queryProjects()`. For a full description of the operations object, see the object returned for the [`items`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-result/items) property in `ProjectsQueryResult`.
   */
  (options?: QueryProjectsOptions): ProjectsQueryBuilder;
}

export function typedQueryProjects(
  httpClient: HttpClient
): TypedQueryProjectsSignature {
  return (query: ProjectQuery, options?: QueryProjectsOptions) =>
    universalTypedQueryProjects(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryProjectsSignature {
  /** */
  (query: ProjectQuery, options?: QueryProjectsOptions): Promise<
    NonNullablePaths<
      QueryProjectsResponse,
      | `projects`
      | `projects.${number}.coverImage.focalPoint.x`
      | `projects.${number}.coverImage.focalPoint.y`
      | `projects.${number}.seoData.settings.preventAutoRedirect`
      | `projects.${number}.watermark.position`
      | `projects.${number}.watermark.size`
      | `projects.${number}.watermark.opacity`
      | `projects.${number}.watermark.imageUrl`
      | `projects.${number}.watermark.enabled`,
      6
    >
  >;
}

export function updateProjectOrderInCollection(
  httpClient: HttpClient
): UpdateProjectOrderInCollectionSignature {
  return (
    identifiers: NonNullablePaths<
      UpdateProjectOrderInCollectionIdentifiers,
      `collectionId` | `projectId`,
      2
    >,
    sortOrder: number
  ) =>
    universalUpdateProjectOrderInCollection(
      identifiers,
      sortOrder,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateProjectOrderInCollectionSignature {
  /**
   * Deprecated - please use ProjectsInCollectionsService.UpdateProjectOrderInCollection instead
   * our Client still use it
   * @param - Index that determines the placement of a project within the collection.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<
      UpdateProjectOrderInCollectionIdentifiers,
      `collectionId` | `projectId`,
      2
    >,
    sortOrder: number
  ): Promise<
    NonNullablePaths<
      UpdateProjectOrderInCollectionResponse,
      | `projectInCollection.collectionId`
      | `projectInCollection.project.coverImage.focalPoint.x`
      | `projectInCollection.project.coverImage.focalPoint.y`
      | `projectInCollection.project.collectionIds`
      | `projectInCollection.project.details`
      | `projectInCollection.project.details.${number}.text`
      | `projectInCollection.project.details.${number}.label`
      | `projectInCollection.project.seoData.tags`
      | `projectInCollection.project.seoData.tags.${number}.type`
      | `projectInCollection.project.seoData.tags.${number}.children`
      | `projectInCollection.project.seoData.tags.${number}.custom`
      | `projectInCollection.project.seoData.tags.${number}.disabled`
      | `projectInCollection.project.seoData.settings.preventAutoRedirect`
      | `projectInCollection.project.seoData.settings.keywords`
      | `projectInCollection.project.seoData.settings.keywords.${number}.term`
      | `projectInCollection.project.seoData.settings.keywords.${number}.isMain`
      | `projectInCollection.project.watermark.position`
      | `projectInCollection.project.watermark.size`
      | `projectInCollection.project.watermark.opacity`
      | `projectInCollection.project.watermark.imageUrl`
      | `projectInCollection.project.watermark.enabled`,
      8
    >
  >;
}

export function queryProjectsWithCollectionInfo(
  httpClient: HttpClient
): QueryProjectsWithCollectionInfoSignature {
  return (query: QueryV2, options?: QueryProjectsWithCollectionInfoOptions) =>
    universalQueryProjectsWithCollectionInfo(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryProjectsWithCollectionInfoSignature {
  /**
   * Deprecated - please use ProjectsInCollectionsService.QueryProjectsInCollections instead
   * our Client still use it
   * @param - WQL expression
   * @deprecated
   */
  (query: QueryV2, options?: QueryProjectsWithCollectionInfoOptions): Promise<
    NonNullablePaths<
      QueryProjectWithCollectionInfoResponse,
      | `projects`
      | `projects.${number}.collectionId`
      | `projects.${number}.project.watermark.position`
      | `projects.${number}.project.watermark.size`
      | `projects.${number}.project.watermark.opacity`
      | `projects.${number}.project.watermark.imageUrl`
      | `projects.${number}.project.watermark.enabled`,
      6
    >
  >;
}

export const onProjectCreated = EventDefinition(
  'wix.portfolio.projects.v1.project_created',
  true,
  (event: ProjectCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.source.lastSync' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.coverImage.focalPoint.x' },
            { path: 'entity.coverImage.focalPoint.y' },
            { path: 'entity.coverImage.unsharpMasking.amount' },
            { path: 'entity.coverImage.unsharpMasking.radius' },
            { path: 'entity.coverImage.unsharpMasking.threshold' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.coverVideo.videoInfo' }],
        },
      ])
    )
)<ProjectCreatedEnvelope>();
export const onProjectDeleted = EventDefinition(
  'wix.portfolio.projects.v1.project_deleted',
  true,
  (event: ProjectDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.source.lastSync' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'undefined.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'undefined.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'undefined.coverImage.focalPoint.x' },
            { path: 'undefined.coverImage.focalPoint.y' },
            { path: 'undefined.coverImage.unsharpMasking.amount' },
            { path: 'undefined.coverImage.unsharpMasking.radius' },
            { path: 'undefined.coverImage.unsharpMasking.threshold' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'undefined.coverVideo.videoInfo' }],
        },
      ])
    )
)<ProjectDeletedEnvelope>();
export const onProjectUpdated = EventDefinition(
  'wix.portfolio.projects.v1.project_updated',
  true,
  (event: ProjectUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.source.lastSync' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.coverImage.focalPoint.x' },
            { path: 'entity.coverImage.focalPoint.y' },
            { path: 'entity.coverImage.unsharpMasking.amount' },
            { path: 'entity.coverImage.unsharpMasking.radius' },
            { path: 'entity.coverImage.unsharpMasking.threshold' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.coverVideo.videoInfo' }],
        },
      ])
    )
)<ProjectUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  ApplicationError,
  Asset,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkUpdateProjectsOptions,
  BulkUpdateProjectsRequest,
  BulkUpdateProjectsResponse,
  BulkUpdateProjectsResult,
  CreateNewPortfolioAppRequest,
  CreateNewPortfolioAppResponse,
  CreateProjectRequest,
  CreateProjectResponse,
  CursorPaging,
  Cursors,
  CustomTag,
  DeleteContext,
  DeleteProjectRequest,
  DeleteProjectResponse,
  DeleteStatus,
  DeletedProjectRestored,
  DetailsLink,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  File,
  GetAdjacentProjectInfoRequest,
  GetAdjacentProjectInfoResponse,
  GetProjectOptions,
  GetProjectPageDataRequest,
  GetProjectPageDataResponse,
  GetProjectRequest,
  GetProjectResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageType,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  Keyword,
  ListProjectsOptions,
  ListProjectsRequest,
  ListProjectsResponse,
  MaskedProject,
  MenuSettingUpdatedEvent,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  Page,
  Pages,
  Paging,
  PagingMetadataV2,
  PicassoAssigned,
  PicassoUnassigned,
  Point,
  Position,
  Project,
  ProjectCoverOneOf,
  ProjectCreatedEnvelope,
  ProjectDeletedEnvelope,
  ProjectDetail,
  ProjectDetailValueOneOf,
  ProjectInfo,
  ProjectQuerySpec,
  ProjectSlug,
  ProjectSource,
  ProjectUpdatedEnvelope,
  ProjectsInCollections,
  ProjectsQueryBuilder,
  ProjectsQueryResult,
  QueryProjectWithCollectionInfoRequest,
  QueryProjectWithCollectionInfoResponse,
  QueryProjectsOptions,
  QueryProjectsRequest,
  QueryProjectsResponse,
  QueryProjectsWithCollectionInfoOptions,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RestoreInfo,
  RestoreProjectFromTrashBinRequest,
  RestoreProjectFromTrashBinResponse,
  SeoSchema,
  ServiceProvisioned,
  ServiceRemoved,
  Settings,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortOrder,
  Sorting,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  SyncProjectWithCollectionMappings,
  SyncStatus,
  Tag,
  URI,
  URIs,
  UnsharpMasking,
  UpdateProject,
  UpdateProjectOrderInCollectionIdentifiers,
  UpdateProjectOrderInCollectionRequest,
  UpdateProjectOrderInCollectionResponse,
  UpdateProjectRequest,
  UpdateProjectResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  Video,
  VideoResolution,
  Watermark,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './portfolio-projects-v1-project-projects.universal.js';
